import type { AnnouncementListQuery, AnnouncementCreateRequest, AnnouncementCommentCreateRequest } from "@saif-reporting/domain/dtos/annoucementDtos.js";
import type { AnnouncementCommentDto } from "@saif-reporting/domain/dtos/models/AnnouncementCommentDto.js";
import { AnnouncementDto } from "@saif-reporting/domain/dtos/models/AnnouncementDto.js";
import type { UserDto } from "@saif-reporting/domain/dtos/models/UserDto.js";
import type { Paginated } from "@saif-reporting/domain/dtos/Paginated.js";
import type { IAnnoucementService } from "@saif-reporting/domain/interfaces/services/IAnnoucementService.js";
import { plainToInstance } from "class-transformer";
import type { ContainerType } from "~/container";
import type { IApi } from "~/interfaces/IApi";

export class AnnoucementService implements IAnnoucementService {
  private readonly api: IApi;
  constructor({ baseApi }: ContainerType) {
    this.api = baseApi;
  }

  async getAll(query: AnnouncementListQuery): Promise<Paginated<AnnouncementDto>> {
    return await this.api.$fetch<Paginated<AnnouncementDto>>("/announcements", { query });
  }

  async getRecent(): Promise<AnnouncementDto[]> {
    return await this.api.$fetch<AnnouncementDto[]>("/announcements/recent");
  }

  async getOne(id: number): Promise<AnnouncementDto> {
    const announcement = await this.api.$fetch<AnnouncementDto>(`/announcements/${id}`);
    return plainToInstance(AnnouncementDto, announcement);
  }

  async getAcknowledgements(id: number): Promise<UserDto[]> {
    const users = await this.api.$fetch<UserDto[]>(`/announcements/${id}/achknowledgements`);
    return users;
  }

  async getComments(id: number): Promise<AnnouncementCommentDto[]> {
    return await this.api.$fetch<AnnouncementCommentDto[]>(`/announcements/${id}/comments`);
  }

  async create(dto: AnnouncementCreateRequest, files: File[]): Promise<AnnouncementDto> {
    const formdata = new FormData();
    files.forEach((file) => {
      formdata.append("files", file);
    });
    formdata.append("message", dto.message);
    dto.mentionsIds.forEach((id) => {
      formdata.append("mentionsIds[]", id.toString());
    });

    const results = await this.api.$fetch<AnnouncementDto>(`/announcements`, {
      method: "post",
      body: formdata,
    });

    return results;
  }

  async delete(id: number): Promise<AnnouncementDto> {
    return await this.api.$fetch<AnnouncementDto>(`/announcements/${id}`, { method: "delete" });
  }

  async acknowledge(id: number): Promise<void> {
    return await this.api.$fetch<void>(`/announcements/${id}/achknowledge`, { method: "post" });
  }

  async addComment(id: number, dto: AnnouncementCommentCreateRequest): Promise<AnnouncementCommentDto> {
    return await this.api.$fetch<AnnouncementCommentDto>(`/announcements/${id}/add-comment`, { method: "post", body: dto });
  }

  async replyToComment(commentId: number, dto: AnnouncementCommentCreateRequest): Promise<AnnouncementCommentDto> {
    return await this.api.$fetch<AnnouncementCommentDto>(`/announcements/comments/${commentId}/reply`, { method: "post", body: dto });
  }

  async getReplies(commentId: number): Promise<AnnouncementCommentDto[]> {
    return await this.api.$fetch<AnnouncementCommentDto[]>(`/announcements/comments/${commentId}/get-replies`);
  }
}
